import { useCallback, useEffect, useRef, useState } from "react";
import { readStoredUser } from "../../utils/sessionUser";

const SAVE_DELAY = 600;
const recordKey = (userId, businessUnitId, importId) => `pilcore:ocr-review-draft:v1:${userId}:${businessUnitId}:${importId}`;
const isObject = (value) => Boolean(value && typeof value === "object" && !Array.isArray(value));
const stringify = (value) => JSON.stringify(value);

export function isOcrReviewDraft(value) {
  return isObject(value) && value.schema_version === 1
    && ["selectedSupplier", "deliveryNumber", "deliveryDate"].every((key) => typeof value[key] === "string")
    && typeof value.dateConfirmed === "boolean"
    && Array.isArray(value.lines) && value.lines.every(isObject)
    && Array.isArray(value.expenses) && value.expenses.every(isObject);
}

function readBackup(key) {
  try {
    const record = JSON.parse(localStorage.getItem(key));
    return record?.version === 1 && isOcrReviewDraft(record.draft) ? record : null;
  } catch {
    return null;
  }
}

function backupRecords(key) {
  try {
    return Object.keys(localStorage).filter((entry) => entry === key || entry.startsWith(`${key}:recovery:`))
      .map((entry) => ({ key: entry, record: readBackup(entry) }))
      .filter(({ record }) => record)
      .sort((a, b) => String(b.record.updated_at || "").localeCompare(String(a.record.updated_at || "")));
  } catch {
    return [];
  }
}

function latestBackup(key) {
  return backupRecords(key).find(({ record }) => !record.dismissed)?.record || null;
}

function readServer(response) {
  const data = response?.data;
  if (!isObject(data) || !Number.isInteger(data.revision) || data.revision < 0
    || typeof data.source_version !== "string" || !data.source_version
    || (data.draft !== null && !isOcrReviewDraft(data.draft))) {
    throw new Error("Invalid review draft response");
  }
  return data;
}

const emptyView = {
  ready: false, initialDraft: null, sourceImport: null, status: "loading", error: null,
  updatedAt: null, hasRecovery: false, safeToLeave: true,
};

// A server revision prevents one tab from replacing another tab's work. The
// synchronous browser copy protects the last keystroke before navigation.
export default function useOcrReviewDraft({ importId, businessUnitId, userId, scopedApi, enabled = true }) {
  const key = recordKey(userId, businessUnitId, importId);
  const session = useRef(null);
  const [view, setView] = useState({ ...emptyView, key });

  useEffect(() => {
    const s = {
      key, api: scopedApi, active: true, completed: false, ready: false,
      revision: null, sourceVersion: null, serverKnown: false, blocked: false,
      current: null, currentJson: null, syncedJson: null, localJson: null,
      recovery: null, timer: null, flight: null, load: null, initialDraft: null,
      sourceImport: null, remoteDraft: null, remoteImport: null,
      writer: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      status: "loading", error: null, updatedAt: null,
    };
    session.current = s;
    const path = `/api/purchasing/ocr-imports/${importId}/review-draft`;
    const dirty = () => s.currentJson !== null && s.currentJson !== s.syncedJson;
    const canLeave = () => s.completed || !dirty() || s.localJson === s.currentJson;
    const emit = (status = s.status, error = s.error) => {
      s.status = status;
      s.error = error;
      if (s.active) setView({
        key, ready: s.ready, initialDraft: s.initialDraft, sourceImport: s.sourceImport, status, error,
        updatedAt: s.updatedAt, hasRecovery: Boolean(s.recovery), safeToLeave: canLeave(),
      });
    };
    const currentScope = () => {
      if (s.api?.isCurrent?.() === true) return true;
      s.blocked = true;
      emit("blocked", "scope");
      return false;
    };
    const canWrite = () => {
      if (!currentScope()) return false;
      const user = readStoredUser();
      if (String(user?.id) === String(userId) && Number(user?.active_business_unit_id) === Number(businessUnitId)
        && (Number(user?.id) === 1 || user?.permissions?.access_settings === true
          || user?.permissions?.["purchasing.delivery_notes.create"] === true)) return true;
      s.blocked = true;
      emit("blocked", "scope");
      return false;
    };
    const preserve = (record, dismissed = false) => {
      if (!record) return true;
      try {
        localStorage.setItem(`${key}:recovery:${record.writer || "previous"}`, stringify({ ...record, dismissed }));
        // Bound the history the user has explicitly set aside. Pending copies
        // from other tabs remain recoverable until saved or dismissed.
        backupRecords(key).filter(({ key: entry, record: value }) => entry !== key && value.dismissed)
          .slice(3).forEach(({ key: entry }) => localStorage.removeItem(entry));
        return true;
      } catch {
        // The active copy remains in its original key if archival is unavailable.
        return false;
      }
    };
    const backup = () => {
      if (!s.current || s.completed) return false;
      try {
        const previous = readBackup(key);
        if (previous && previous.writer !== s.writer && stringify(previous.draft) !== s.currentJson) {
          // Keep a different tab's unsynchronised copy before replacing the
          // shared "latest draft" pointer.
          if (!preserve(previous)) throw new Error("Could not preserve another tab's draft");
        }
        localStorage.setItem(key, stringify({
          version: 1, writer: s.writer, draft: s.current,
          revision: s.revision, source_version: s.sourceVersion,
          updated_at: new Date().toISOString(),
        }));
        s.localJson = s.currentJson;
        return true;
      } catch {
        s.localJson = null;
        return false;
      }
    };
    const removeOwnBackup = () => {
      try {
        backupRecords(key).forEach(({ key: entry, record }) => {
          if (record.writer === s.writer
            || (record.source_version === s.sourceVersion && stringify(record.draft) === s.syncedJson)) {
            localStorage.removeItem(entry);
          }
        });
      } catch {
        // Server acknowledgement is already durable.
      }
      s.localJson = null;
    };
    const schedule = () => {
      clearTimeout(s.timer);
      if (!s.completed && !s.blocked && s.serverKnown) {
        s.timer = setTimeout(() => { s.flush(); }, SAVE_DELAY);
      }
    };
    const serverFailure = (error, phase) => {
      const code = error?.response?.data?.code || error?.code;
      if (code === "OCR_DRAFT_SOURCE_CHANGED") {
        s.blocked = true;
        s.recovery = null;
        emit("blocked", "source");
      } else if (code === "OCR_DRAFT_CONFLICT") {
        s.blocked = true;
        emit("conflict", "revision");
      } else if (code === "OCR_DRAFT_IMPORT_UNAVAILABLE" || code === "OCR_DRAFT_IMPORT_NOT_FOUND") {
        s.blocked = true;
        emit("blocked", "unavailable");
      } else if (code === "OCR_BUSINESS_UNIT_CHANGED" || !currentScope()) {
        s.blocked = true;
        emit("blocked", "scope");
      } else {
        emit(s.localJson === s.currentJson && dirty() ? "local" : "error", phase);
      }
    };
    s.canLeave = canLeave;
    s.change = (value) => {
      if (s.completed || !s.ready || !isOcrReviewDraft(value)) return;
      const json = stringify(value);
      if (json === s.currentJson) return;
      s.current = JSON.parse(json);
      s.currentJson = json;
      if (!dirty()) {
        removeOwnBackup();
        emit("saved", null);
        return;
      }
      const durable = backup();
      if (s.blocked) emit(s.status, s.error);
      else emit(durable ? "local" : "error", durable ? (s.serverKnown ? null : "load") : "storage");
      schedule();
    };
    s.flush = async () => {
      clearTimeout(s.timer);
      if (s.load) await s.load;
      if (s.completed || !dirty()) return true;
      if (s.flight) {
        const saved = await s.flight;
        if (!saved) return false;
        return s.completed || !dirty() ? true : (s.blocked || !s.serverKnown ? false : s.flush());
      }
      if (s.blocked || !s.serverKnown || !canWrite()) return false;
      const sentJson = s.currentJson;
      const sentDraft = JSON.parse(sentJson);
      emit("saving", s.localJson === s.currentJson ? null : "storage");
      let successful = false;
      s.flight = (async () => {
        try {
          const result = readServer(await s.api.put(path, {
            draft: sentDraft, revision: s.revision, source_version: s.sourceVersion,
          }));
          if (s.completed || !currentScope()) return false;
          s.revision = result.revision;
          s.sourceVersion = result.source_version;
          s.updatedAt = result.updated_at || new Date().toISOString();
          s.syncedJson = sentJson;
          if (dirty()) {
            const durable = backup();
            emit(durable ? "local" : "error", durable ? null : "storage");
          } else {
            removeOwnBackup();
            emit("saved", null);
          }
          successful = true;
          return true;
        } catch (error) {
          if (!s.completed) serverFailure(error, "save");
          return false;
        }
      })();
      await s.flight;
      s.flight = null;
      // Serialise writes; a slow acknowledgement must never discard changes
      // typed while that request was in flight.
      if (successful && dirty() && s.active && !s.completed) return s.flush();
      return successful;
    };
    s.loadDraft = async () => {
      if (s.load) return s.load;
      while (s.flight) await s.flight;
      if (s.load) return s.load;
      if (s.completed || !currentScope()) return false;
      let local = dirty() ? {
        version: 1, writer: s.writer, draft: s.current,
        revision: s.revision, source_version: s.sourceVersion,
      } : latestBackup(key);
      s.load = (async () => {
        try {
          const data = readServer(await s.api.get(path));
          if (!s.active || s.completed || !currentScope()) return false;
          const initialise = !s.ready;
          if (data.import_data && (String(data.import_data.id) !== String(importId)
            || Number(data.import_data.business_unit_id) !== Number(businessUnitId))) {
            throw new Error("Mismatched review import");
          }
          const remoteJson = data.draft === null ? null : stringify(data.draft);
          // A retry can run while the user keeps typing. Re-read the live state
          // after the request rather than reinstating its earlier snapshot.
          if (s.ready && dirty()) {
            local = {
              version: 1, writer: s.writer, draft: s.current,
              revision: s.revision, source_version: s.sourceVersion,
            };
          } else {
            local = backupRecords(key).find(({ record }) => !record.dismissed
              && record.source_version === data.source_version && stringify(record.draft) !== remoteJson)?.record || local;
          }
          s.remoteDraft = data.draft;
          s.remoteImport = data.import_data || null;
          if (initialise) s.sourceImport = s.remoteImport;
          s.revision = data.revision;
          s.sourceVersion = data.source_version;
          s.serverKnown = true;
          s.blocked = false;
          s.recovery = null;
          s.updatedAt = data.updated_at || null;
          s.syncedJson = remoteJson;
          const matching = local && !data.source_changed
            && local.source_version === data.source_version && local.revision === data.revision;
          const unknownButEmpty = local && !data.source_changed && local.source_version === null
            && local.revision === null && data.draft === null && data.revision === 0;
          if (local && (matching || unknownButEmpty)) {
            s.current = local.draft;
            s.currentJson = stringify(local.draft);
            s.localJson = s.currentJson;
            // Refresh the base of a draft first written while the server was
            // unreachable, now that we know no server draft existed.
            backup();
            if (initialise) s.initialDraft = local.draft;
          } else if (local && (data.source_changed || (local.source_version && local.source_version !== data.source_version))) {
            const existing = readBackup(key);
            if (preserve(local, true) && existing && stringify(existing.draft) === stringify(local.draft)) {
              try { localStorage.removeItem(key); } catch { /* Preserve the original if removal fails. */ }
            }
            s.recovery = null;
            if (initialise) {
              s.current = data.draft;
              s.currentJson = s.syncedJson;
              s.initialDraft = data.draft;
            } else {
              s.blocked = true;
            }
          } else if (local && stringify(local.draft) !== s.syncedJson) {
            s.recovery = local;
            s.blocked = true;
            if (initialise) {
              s.current = data.draft;
              s.currentJson = s.syncedJson;
              s.initialDraft = data.draft;
            }
          } else if (initialise) {
            s.current = data.draft;
            s.currentJson = s.syncedJson;
            s.initialDraft = data.draft;
          }
          s.ready = true;
          if (s.blocked) emit(s.recovery ? "conflict" : "blocked", s.recovery ? "revision" : "source");
          else if (s.recovery) emit("conflict", "revision");
          else emit(dirty() ? "local" : (data.draft ? "saved" : "idle"), null);
          if (dirty() && !s.blocked) schedule();
          return true;
        } catch (error) {
          if (!s.active || s.completed) return false;
          if (!s.ready) {
            s.initialDraft = local?.draft || null;
            s.current = s.initialDraft;
            s.currentJson = s.current === null ? null : stringify(s.current);
            s.localJson = local ? s.currentJson : null;
            if (local) {
              s.revision = local.revision;
              s.sourceVersion = local.source_version;
            }
          }
          // Without a local copy there is nothing safe to edit: a failed GET
          // may be hiding an existing server draft. Keep the editor closed.
          s.ready = s.ready || Boolean(local);
          serverFailure(error, "load");
          return false;
        }
      })();
      const result = await s.load;
      s.load = null;
      return result;
    };
    s.recover = async () => {
      if (!s.recovery || s.completed || !currentScope()) return null;
      const value = s.recovery.draft;
      s.recovery = null;
      s.blocked = false;
      s.current = null;
      s.currentJson = null;
      s.change(value);
      return JSON.parse(stringify(value));
    };
    s.useRemote = async () => {
      if (!s.recovery || s.completed || !s.serverKnown || !currentScope()) return null;
      const discardedJson = stringify(s.recovery.draft);
      backupRecords(key).filter(({ record }) => stringify(record.draft) === discardedJson)
        .forEach(({ key: entry, record }) => {
          if (preserve(record, true) && entry === key) {
            try { localStorage.removeItem(key); } catch { /* Keep the original if removal fails. */ }
          }
        });
      s.current = s.remoteDraft;
      s.currentJson = s.syncedJson;
      s.localJson = null;
      s.recovery = null;
      s.blocked = false;
      s.sourceImport = s.remoteImport;
      emit(s.remoteDraft ? "saved" : "idle", null);
      return s.current === null ? null : JSON.parse(stringify(s.current));
    };
    s.complete = () => {
      s.completed = true;
      clearTimeout(s.timer);
      try {
        // Creation makes all earlier drafts of this import obsolete.
        Object.keys(localStorage).filter((entry) => entry === key || entry.startsWith(`${key}:recovery:`))
          .forEach((entry) => localStorage.removeItem(entry));
      } catch {
        // Terminal import status prevents any stale backup from being saved.
      }
      s.recovery = null;
      emit("saved", null);
    };
    const beforeUnload = (event) => {
      if (canLeave()) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", beforeUnload);
    if (enabled) {
      emit();
      s.loadDraft();
    } else {
      s.ready = true;
      s.completed = true;
      emit("idle", null);
    }
    return () => {
      clearTimeout(s.timer);
      if (dirty() && !s.completed && !s.blocked) s.flush();
      s.active = false;
      window.removeEventListener("beforeunload", beforeUnload);
    };
  }, [key, importId, businessUnitId, userId, scopedApi, enabled]);

  const onChange = useCallback((draft) => session.current?.change(draft), []);
  const flush = useCallback(() => session.current?.flush() || Promise.resolve(true), []);
  const retry = useCallback(() => session.current?.loadDraft() || Promise.resolve(false), []);
  const recover = useCallback(() => session.current?.recover() || Promise.resolve(null), []);
  const useRemote = useCallback(() => session.current?.useRemote() || Promise.resolve(null), []);
  const complete = useCallback(() => session.current?.complete(), []);
  const canLeave = useCallback(() => session.current?.canLeave() ?? true, []);
  return {
    ...(view.key === key ? view : emptyView),
    onChange, flush, retry, recover, useRemote, useSaved: useRemote, complete, canLeave,
  };
}
